import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MegaMenuColumn from './MegaMenuColumn';
import useMegaMenuDismiss from '../hooks/useMegaMenuDismiss';
import useMegaMenuPosition from '../hooks/useMegaMenuPosition';
import { stripHtmlToText } from '../utils/navItemUtils';

// Elements a keyboard user can land on while tabbing through a mega menu's
// content — used both to find the first item to focus on keyboard-open and to
// detect the first/last item for Tab/Shift+Tab containment at the panel edges.
const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MegaMenu = ({
	item,
	visible = true,
	onClose,
	triggerRef,
	autoFocusFirstItem = false,
	onAutoFocusHandled = () => {},
	onFocusNextTrigger = () => false,
	onFocusLeave = () => {}
}) => {
	const menuRef = useRef(null);
	const topPx = useMegaMenuPosition(menuRef, visible);
	const { onMouseLeave } = useMegaMenuDismiss({
		menuRef,
		onClose,
		visible,
		triggerRef
	});

	// Enter/Space on the trigger opens the menu via native onClick and, only
	// for that keyboard-activated open (never a mouse click), moves focus onto
	// the menu's first item. onAutoFocusHandled resets the parent's pending
	// flag so a later re-render (or reopen by mouse) doesn't refocus.
	useEffect(() => {
		if (!visible || !autoFocusFirstItem) return;
		const firstFocusable =
			menuRef.current?.querySelector(FOCUSABLE_SELECTOR);
		firstFocusable?.focus();
		onAutoFocusHandled();
	}, [visible, autoFocusFirstItem, onAutoFocusHandled]);

	// Tab from the last item / Shift+Tab from the first item would otherwise
	// land wherever DOM order puts it next (all triggers render before all
	// panels, so that's never "the next top-level trigger" or "the opening
	// trigger"). Redirect focus explicitly instead of relying on tab order;
	// the resulting blur (handled below) is what actually closes the menu.
	const handleKeyDown = (event) => {
		if (event.key !== 'Tab') return;
		const el = menuRef.current;
		if (!el) return;

		const focusable = Array.from(el.querySelectorAll(FOCUSABLE_SELECTOR));
		if (focusable.length === 0) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (!event.shiftKey && event.target === last) {
			if (onFocusNextTrigger(item.id)) event.preventDefault();
		} else if (event.shiftKey && event.target === first) {
			if (triggerRef?.current) {
				event.preventDefault();
				triggerRef.current.focus();
			}
		}
	};

	// General rule: whenever focus leaves the panel's DOM subtree, by any
	// path (not just the Tab/Shift+Tab edges above), close the menu. onBlur
	// bubbles in React, so this fires once for the descendant that actually
	// lost focus. Uses onFocusLeave (a compare-and-swap close), not onClose
	// (unconditional) — see handleFocusLeave's own comment for why.
	const handleBlur = (event) => {
		const el = menuRef.current;
		if (!el) return;
		if (!event.relatedTarget || !el.contains(event.relatedTarget)) {
			onFocusLeave(item.id);
		}
	};

	const columns = item.columns || [];
	if (columns.length === 0) return null;

	const gridTemplateColumns = columns
		.map((c) => `minmax(0, ${c.gridUnits}fr)`)
		.join(' ');

	return (
		<>
			{visible && (
				<>
					{/* Full-viewport scrim below the header. Besides the usual
					dim-the-page-behind-the-flyout treatment, its opaque
					background also covers the same scrollbar-width gap over
					normal page content (e.g. a hero banner) for the rest of
					the viewport.
					Height is computed via 100vh rather than a CSS `bottom: 0`
					constraint -- some host pages give position:fixed elements
					here a containing block shorter than the real viewport
					(same category of CMS-scaffolding quirk as the position
					fix below), which silently zeroed out a bottom-anchored
					height. 100vh always resolves against the true viewport
					regardless of containing block, matching how `maxHeight`
					below is computed. */}
					<div
						className="ws-nav-drawer__mega-menu-backdrop"
						style={{
							top: `${topPx}px`,
							height: `calc(100vh - ${topPx}px)`
						}}
						aria-hidden="true"
					/>
				</>
			)}
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- delegates events bubbled from its own focusable descendants */}
			<div
				id={`mega-menu-${item.id}`}
				ref={menuRef}
				className="ws-nav-drawer__mega-menu"
				style={{
					top: `${topPx}px`,
					display: visible ? '' : 'none',
					maxHeight: `calc(100vh - ${topPx}px)`,
					overflowY: 'auto'
				}}
				aria-hidden={!visible}
				onMouseLeave={onMouseLeave}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
			>
				<div
					className="ws-nav-drawer__mega-menu__content"
					role="region"
					aria-label={stripHtmlToText(item.label)}
					style={{ gridTemplateColumns }}
				>
					{columns.map((column, idx) => (
						<MegaMenuColumn
							// Index-as-key is safe here: columns are always
							// rendered directly from their parent item's own
							// `columns` array in a fixed order and are never
							// independently reordered or filtered. The prior
							// gridUnits+firstItemId key could collide between
							// two columns with the same gridUnits and no nav
							// items.
							// eslint-disable-next-line react/no-array-index-key
							key={idx}
							column={column}
							parentLabel={item.label}
							onLinkClick={onClose}
						/>
					))}
				</div>
			</div>
		</>
	);
};

MegaMenu.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		columns: PropTypes.arrayOf(
			PropTypes.shape({
				gridUnits: PropTypes.number.isRequired,
				navigationItems: PropTypes.arrayOf(PropTypes.shape({}))
			})
		)
	}).isRequired,
	visible: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	autoFocusFirstItem: PropTypes.bool,
	onAutoFocusHandled: PropTypes.func,
	onFocusNextTrigger: PropTypes.func,
	onFocusLeave: PropTypes.func
};

MegaMenu.defaultProps = {
	visible: true,
	triggerRef: null,
	autoFocusFirstItem: false,
	onAutoFocusHandled: () => {},
	onFocusNextTrigger: () => false,
	onFocusLeave: () => {}
};

export default MegaMenu;
